/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isArray } from '@ember/array';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import { mapBy, resolved } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';
import moment from 'moment';

export default class MatrixController extends Controller {
  queryParams = [
    {
      currentTab: { as: 'matrix-currentTab' },
    },
  ];

  // Services
  @service
  options;

  @service
  session;

  @service
  store;

  @optionAlias('hero:lock:milestones')
  milestonesLocked;

  @alias('model.group.currentLearners.length')
  numLearners;

  @computed('model.{goals.[],states.[]}')
  get meta() {
    return this.model.goals.map((goal) => {
      let goalId = goal.get('id');
      let states = this.model.states.filter((item) => item.goal_id === goalId);
      let data = {
        goalId,
        developing: this.calculatePercentage(
          states.filter((item) => item.awarded === GoalState.DEVELOPING).length,
        ),
        secure: this.calculatePercentage(
          states.filter((item) => item.awarded === GoalState.SECURE).length,
        ),
        states,
      };

      return data;
    });
  }

  calculatePercentage(amount) {
    let numLearners = this.numLearners;
    if (isArray(amount)) {
      amount = amount.length;
    }
    return numLearners ? (amount / numLearners) * 100 : 0;
  }

  @computed('meta.@each.secure')
  get completedCategoryIds() {
    return mapBy(
      this.model.categories.filter((category) => {
        // resolve the async hasMany relationship upfront.
        return resolved(category.goals).every((goal) => {
          let meta = this.meta.find((item) => item.goalId === goal.id);
          if (isEmpty(meta)) {
            return false;
          }
          return Math.round(meta.secure) === 100;
        });
      }),
      'id',
    );
  }

  @tracked
  selectedCategory = null;
  @tracked
  markCategoryDialogOpen = false;
  @tracked
  markCategoryComplete = false;
  @tracked
  processingCategory = false;
  @tracked numProcessed = 0;

  @computed('selectedCategory.goals.length', 'numLearners')
  get numToProcess() {
    if (isEmpty(this.selectedCategory)) {
      return 0;
    }
    return this.selectedCategory.goals.length * this.numLearners;
  }

  @computed('numProcessed', 'numToProcess')
  get processedPercent() {
    if (this.numToProcess > 0) {
      return (this.numProcessed / this.numToProcess) * 100;
    }
    return 0;
  }

  @tracked
  numMarkAllErrors = 0;

  // peak time definitions
  nowInterval = moment.duration(1, 'minutes');

  peakInterval = moment.duration(1, 'hours');
  peakFormat = 'HH:mm';
  peakPrecision = 'minute';
  morningPeakStart = '08:45';
  morningPeakEnd = '09:15';

  @tracked
  goalDialogOpen;

  @tracked
  selectedGoal;

  @tracked
  selectedGoalStates;

  @action
  noOp() {}

  @action
  openGoalDialog(goal, event) {
    event.preventDefault();
    this.selectedGoal = goal;
    // Although we already have the states, providing a fresh query allows the group-goal-setter to refresh
    // just the states for this goal, rather than having to refresh the states for ALL goals in the subject
    // every time a goal state is changed.  Closing the dialog will perform the full reload.
    this.selectedGoalStates = this.store.query('goals/state', {
      groupId: this.model.group.id,
      goalId: goal.id,
      current: true,
    });
    this.goalDialogOpen = true;
  }

  @action
  closeGoalDialog() {
    this.goalDialogOpen = false;
    this.model.states.update();
  }

  @action
  handleMarkCategoryComplete(category) {
    this.selectedCategory = category;
    this.processingCategory = false;
    this.numProcessed = 0;
    this.markCategoryDialogOpen = true;
    this.markCategoryComplete = false;
  }

  @action
  closeMarkCategoryDialog() {
    this.markCategoryDialogOpen = false;
  }

  @action
  confirmMarkCategoryComplete() {
    this.processingCategory = true;
    this.numMarkAllErrors = 0;

    let learners = this.model.group.currentLearners;
    let promises = [];
    this.selectedCategory.goals.forEach((goal) => {
      let states = this.model.states.filter((item) => item.goal_id === goal.id);

      learners.forEach((learner) => {
        let personId = learner.personId;
        let state = states.find((item) => item.person_id === personId);
        if (isEmpty(state) || state.awarded !== GoalState.SECURE) {
          let newState = this.store.createRecord('goals/state', {
            schoolId: this.get('session.school'),
            assessorId: this.get('session.person'),
            personId: learner.get('person'),
            goalId: goal,
            awarded: GoalState.SECURE,
          });

          promises.push(
            saveOrDiscard(newState)
              .catch(() => {
                this.incrementProperty('numMarkAllErrors');
              })
              .finally(() => {
                this.incrementProperty('numProcessed');
              }),
          );
        } else {
          this.incrementProperty('numProcessed');
        }
      });
    });

    Promise.all(promises).then(() => {
      this.processingCategory = false;
      this.model.states.update();
    });
  }
}
